// includes/auth/paymentsApi.js
//
// API layer for Payment & Withdrawal Settings, checkout methods (PayPal,
// Apple Pay, Google Pay), the Wallet (balance, earnings, withdrawals) and
// Banking (bank accounts, PayPal receiver). Server: elakay_routes/payments.js
// (plain JSON -- same apiFetch shape as transactionCenterApi.js).
import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { buildQuery } from "./transactionCenterApi";

const getToken = () => isAuthenticated()?.token || "";

export const apiFetch = async (path, { method = "GET", params, body, headers = {}, token, signal } = {}) => {
  const finalHeaders = { Accept: "application/json", ...headers };
  const resolved = token || getToken();
  if (resolved) finalHeaders.Authorization = `Bearer ${resolved}`;
  if (body !== undefined) finalHeaders["Content-Type"] = "application/json";
  const response = await fetch(`${elakay_API}${path}${buildQuery(params)}`, {
    method, headers: finalHeaders, body: body === undefined ? undefined : JSON.stringify(body), signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const error = new Error(data.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.code = data.code || "";
    error.reason = data.reason || "";
    error.details = data.details;
    throw error;
  }
  return data;
};

const newIdempotencyKey = () =>
  window.crypto?.randomUUID?.() || `wd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;

// Settings
export const getAvailablePaymentMethodsAPI = (opts) => apiFetch("/payments/methods", opts);
export const getFamilyPaymentSettingsAPI = (opts) => apiFetch("/family/payment-settings", opts);
export const updateFamilyPaymentSettingsAPI = (body, opts) => apiFetch("/family/payment-settings", { method: "PUT", body, ...opts });

// Checkout
export const quoteCheckoutAPI = (body, opts) => apiFetch("/order/quote", { method: "POST", body, ...opts });
export const attachWalletMethodAPI = (body, opts) => apiFetch("/payments/wallet-method", { method: "POST", body, ...opts });
export const authorizePayPalOrderAPI = (paypalOrderId, opts) => apiFetch(`/payments/paypal/orders/${encodeURIComponent(paypalOrderId)}/authorize`, { method: "POST", body: {}, ...opts });
export const cancelPayPalOrderAPI = (paypalOrderId, opts) => apiFetch(`/payments/paypal/orders/${encodeURIComponent(paypalOrderId)}/cancel`, { method: "POST", body: {}, ...opts });

export const updateOrderLifecycleAPI = (orderId, lineId, body, opts) => apiFetch(`/order/${encodeURIComponent(orderId)}/${encodeURIComponent(lineId)}/lifecycle`, { method: "POST", body, ...opts });
// Buyer, pending order: replace its card (same order/amounts, never a capture).
export const changeOrderPaymentMethodAPI = (orderId, lineId, paymentMethodId, opts) => apiFetch(`/order/${encodeURIComponent(orderId)}/${encodeURIComponent(lineId)}/payment-method`, { method: "POST", body: { paymentMethodId }, ...opts });
// Buyer: finish a pending bank step on the order's existing payment, or create
// the PayPal approval of a PayPal order.
export const confirmOrderPaymentAPI = (orderId, lineId, opts) => apiFetch(`/order/${encodeURIComponent(orderId)}/${encodeURIComponent(lineId)}/authorize`, { method: "POST", body: {}, ...opts });

// Wallet
export const getWalletBalanceAPI = (opts) => apiFetch("/wallet/balance", opts);
export const getWalletEarningsAPI = (params, opts) => apiFetch("/wallet/earnings", { params, ...opts });
export const getWalletWithdrawalsAPI = (params, opts) => apiFetch("/wallet/withdrawals", { params, ...opts });
export const quoteWithdrawalAPI = (body, opts) => apiFetch("/wallet/withdrawals/quote", { method: "POST", body, ...opts });
// One key per confirmed withdrawal attempt: a network retry of the SAME
// attempt reuses it, so the server never creates a second withdrawal.
export const createWithdrawalAPI = (body, idempotencyKey = newIdempotencyKey(), opts) =>
  apiFetch("/wallet/withdrawals", { method: "POST", body, headers: { "Idempotency-Key": idempotencyKey }, ...opts });
export const refreshWithdrawalAPI = (id, opts) => apiFetch(`/wallet/withdrawals/${encodeURIComponent(id)}/refresh`, { method: "POST", body: {}, ...opts });
export { newIdempotencyKey };

// Banking
export const getWithdrawalBankAccountsAPI = (opts) => apiFetch("/wallet/bank-accounts", opts);
export const getPayPalRecipientAPI = (opts) => apiFetch("/wallet/paypal-recipient", opts);
export const setPayPalRecipientAPI = (body, opts) => apiFetch("/wallet/paypal-recipient", { method: "PUT", body, ...opts });
export const removePayPalRecipientAPI = (password, opts) => apiFetch("/wallet/paypal-recipient", { method: "DELETE", body: { password }, ...opts });

// Family
export const getBlockedEarningsAPI = (params, opts) => apiFetch("/family/earnings/blocked", { params, ...opts });
export const resolveBlockedEarningAPI = (id, body, opts) => apiFetch(`/family/earnings/${encodeURIComponent(id)}/resolve`, { method: "POST", body, ...opts });

// Family: Elakay's PayPal Business connection + provider diagnostics
export const getPayPalBusinessConfigAPI = (opts) => apiFetch("/family/payment-providers/paypal", opts);
export const savePayPalBusinessConfigAPI = (body, opts) => apiFetch("/family/payment-providers/paypal", { method: "PUT", body, ...opts });
export const removePayPalBusinessConfigAPI = (password, opts) => apiFetch("/family/payment-providers/paypal", { method: "DELETE", body: { password }, ...opts });
export const testPayPalConnectionAPI = (opts) => apiFetch("/family/payment-providers/paypal/test", { method: "POST", body: {}, ...opts });
export const getStripeProviderStatusAPI = (opts) => apiFetch("/family/payment-providers/stripe/status", opts);

// Checkout: after a 3-D Secure step, the server re-reads Stripe's real state.
export const syncStripeOrderAPI = (orderId, opts) => apiFetch(`/payments/stripe/orders/${encodeURIComponent(orderId)}/sync`, { method: "POST", body: {}, ...opts });
